<template>
  <div
    class="footer-nav w-full relative bottom-0 inset-x-0 z-30 mt-4 md:mb-0 mb-[32px] md:ps-0 px-[20px]"
  >
    <div class="flex justify-start gap-2 items-center" v-if="footerResponse.length > 0">
      
      <div
        class="w-8 h-8 flex justify-center items-center"
        v-html="starSvg({ width: 16, height: 16, color: '#05A8F4' })"
      ></div>
      <div
        class="flex item-center text-[20px] font-just font-500 text-btnActive"
      >
        {{ $t('layoutLabels.footerTitle') }}
      </div>
    </div>
    <div
      class="flex items-center gap-4 overflow-x-auto hide-scroll flex-nowrap scroll-smooth py-4"
    >
      <template v-if="loading">
        <BaseSkeleton
          v-for="n in 6"
          :key="n"
          width="312px"
          height="253px"
          wrapper-class="flex-shrink-0 rounded-2xl"
        />
      </template>
      <template v-else>
        <router-link
          v-for="(card, index) in footerResponse"
          :key="card.taskSlug"
          :to="getRoutePath(card)"
          :class="[
            'flex flex-col p-4 rounded-2xl cursor-pointer flex-shrink-0 w-[308px] transition-all duration-300 h-32 gap-4 no-underline',
            getCardBackground(index),
          ]"
          @click.prevent="cardClick(card)"
          @click="handleSubscriptionPopup(card)"
          :aria-label="$t(`tasks.${card.taskSlug}.title`, card.title)"
        >
          <div class="flex items-center gap-2 flex-1">
            <img
              class="icon-placeholder flex justify-center items-center w-8 h-8"
              :src="card.icon"
              :alt="$t(`tasks.${card.taskSlug}.title`, card.title)"
            />
            <div
              class="font-just text-xl font-normal text-btnActive overflow-hidden text-ellipsis line-clamp-2"
            >
              {{ $t(`tasks.${card.taskSlug}.title`, card.title) }}
            </div>
          </div>
          <div class="flex justify-between items-center w-full">
            <div class="font-just text-base font-normal text-btnActive">
              {{ $t(`tasks.${card.taskSlug}.actionMessage`, card.actionMessage) }}
            </div>
            <div
              class="w-4 h-4 flex justify-center items-center"
              v-html="rightArrow({ width: 24, height: 24, color: '#1E1E1E' })"
            ></div>
          </div>
        </router-link>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ROUTE_PATHS } from "@/router/constants";
import {
  profile,
  digitalVault,
  groupUserSvg,
  legacy,
  estatePlan,
  dashboard,
  rightArrow,
  starSvg,
} from "@/utils/commonSvg";
import { onMounted, ref } from "vue";
import { useRightPanelStore } from "@/stores/layoutStore";
import { useUserStore } from "@/stores/userStore";

// Store for layout data
const layoutStore = useRightPanelStore();
const userStore = useUserStore();

// State for footer cards
const footerResponse = ref([]);
const loading = ref(true);

// Fetch footer data on mount
onMounted(async () => {
  await layoutStore.getFooterData();
  footerResponse.value = layoutStore.footerData || [];
  loading.value = false;
});

// Helper function to handle role-based routing
const getRoleRoute = (taskRequirementTarget) => {
  const roleRouteMap = {
    executor: ROUTE_PATHS.EXECUTOR,
    trustee: ROUTE_PATHS.TRUSTEE,
    guardian: ROUTE_PATHS.GUARDIAN,
    poa_financial: ROUTE_PATHS.ACCOUNT_AGENT,
    poa_health: ROUTE_PATHS.ACCOUNT_AGENT,
  };
  return roleRouteMap[taskRequirementTarget] || ROUTE_PATHS.ACCOUNT_AGENT;
};

// Helper function to handle wishes-based routing
const getWishesRoute = (taskRequirementTarget) => {
  const wishesRouteMap = {
    "bequest-asset": ROUTE_PATHS.ASSETS_BEQUEST,
    "bequest-cash": ROUTE_PATHS.CASH_BEQUEST,
    "final-service": ROUTE_PATHS.FUNERAL_PLANS,
    remains: ROUTE_PATHS.BODY_HANDLING,
  };
  return wishesRouteMap[taskRequirementTarget] || ROUTE_PATHS.ESTATE_PLAN_WISHES;
};

// Helper function to handle assets section routing
const getAssetsRoute = (taskSlug) => {
  if (taskSlug === "create_object_myestate_onboarding") {
    return ROUTE_PATHS.ESTATE_PLAN_ASSET;
  }
  return ROUTE_PATHS.DIGITAL_VAULT;
};

// Helper function to handle circle section routing
const getCircleRoute = (card) => {
  const { taskSlug, taskRequirementType, taskRequirementTarget } = card;

  if (taskSlug === "create_object_mycircle_onboarding") {
    return ROUTE_PATHS.ESTATE_PLAN_CIRCLE;
  }

  if (taskRequirementType === "role") {
    return getRoleRoute(taskRequirementTarget);
  }

  return ROUTE_PATHS.ESTATE_PLAN_CIRCLE;
};

// Helper function to handle wishes section routing
const getWishesRouteFromCard = (card) => {
  const { taskSlug, taskRequirementType, taskRequirementTarget } = card;

  if (taskSlug === "create_object_mywishes_onboarding") {
    return ROUTE_PATHS.ESTATE_PLAN_WISHES;
  }

  if (taskRequirementType === "wishes") {
    return getWishesRoute(taskRequirementTarget);
  }

  return ROUTE_PATHS.ESTATE_PLAN_WISHES;
};

// Helper function to handle estate module routing
const getEstateRoute = (card) => {
  const { moduleSectionSlug } = card;

  if (moduleSectionSlug === "assets") {
    return getAssetsRoute(card.taskSlug);
  }

  if (moduleSectionSlug === "circle") {
    return getCircleRoute(card);
  }

  if (moduleSectionSlug === "wishes") {
    return getWishesRouteFromCard(card);
  }

  return ROUTE_PATHS.DASHBOARD;
};

// Helper function to handle encryption module routing
const getEncryptionRoute = (taskRequirementTarget) => {
  return taskRequirementTarget === "agent"
    ? ROUTE_PATHS.ACCOUNT_AGENT
    : ROUTE_PATHS.SECURITY;
};

// Helper function to handle subscription routing
const getSubscriptionRoute = (taskRequirementTarget) => {
  return taskRequirementTarget === "licenses"
    ? ROUTE_PATHS.PROFILE
    : ROUTE_PATHS.DASHBOARD;
};

// Helper function to handle legacy module routing
const getLegacyRoute = (taskSlug) => {
  return taskSlug === "create_object_mylegacy_onboarding"
    ? ROUTE_PATHS.LEGACY
    : ROUTE_PATHS.DASHBOARD;
};

// Main routing function with reduced complexity
const getRoutePath = (card) => {
  const {
    moduleSlug,
    taskRequirementType,
    taskRequirementTarget,
  } = card;

  // Handle encryption module
  if (moduleSlug === "encryption") {
    return getEncryptionRoute(taskRequirementTarget);
  }

  // Handle documents module
  if (moduleSlug === "documents") {
    return ROUTE_PATHS.DOCUMENTS;
  }

  // Handle subscription tasks
  if (taskRequirementType === "subscription") {
    return getSubscriptionRoute(taskRequirementTarget);
  }

  // Handle estate module
  if (moduleSlug === "estate") {
    return getEstateRoute(card);
  }

  // Handle legacy module
  if (moduleSlug === "legacy") {
    return getLegacyRoute(card.taskSlug);
  }

  // Handle subscribe module
  if (moduleSlug === "subscribe") {
    return ROUTE_PATHS.PROFILE;
  }

  // Default fallback
  return ROUTE_PATHS.DASHBOARD;
};

/**
 * Handle subscription popup for profile/subscription related cards
 */
const handleSubscriptionPopup = (card) => {
  const routePath = getRoutePath(card);
  if (
    routePath === ROUTE_PATHS.PROFILE &&
    userStore.loginResponse?.licenses?.length === 0
  ) {
    userStore.subscriptionMsgPopUp = true;
  }
};

// Alternate card background color
const getCardBackground = (index) => {
  return index % 2 === 0
    ? "bg-gradient-to-r from-[#FFEAE1] to-[#E8F3FA]"
    : "bg-gradient-to-r from-[#83DAFF] to-[#8FFFD0]";
};

// Return icon for module key
const getIcon = (moduleKey) => {
  switch (moduleKey) {
    case "USER_ON_BOARDING":
      return profile;
    case "DIGITAL_VAULT":
      return digitalVault;
    case "ACCOUNT_AGENT":
      return groupUserSvg;
    case "LEGACY":
      return legacy({ width: "40", height: "40" });
    case "MY_ESTATE_PLAN":
      return estatePlan;
    default:
      return dashboard;
  }
};

// Emit events for special card clicks
const emit = defineEmits(["passphraseClick", "subscribeClick"]);
const cardClick = (card) => {
  if (card.taskSlug === "encryption_create") {
    emit("passphraseClick", card.taskSlug);
  } else if (card.taskRequirementType === "subscription") {
    emit("subscribeClick", card.taskSlug);
  }
};
</script>